import { MemberMarkers } from "@prototypes/member-markers/MemberMarkers";
import classNames from "classnames";
import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useHistory } from "react-router-dom";
import { useAgentsMap } from "../../common/hooks/useAgents";
import { useMembersMap } from "../../common/hooks/useMembers";
import type { Agent } from "../../common/types/Agent";
import { signOut } from "../../Firebase";
import routes from "../../utils/routes";
import { sortTasksInProfile } from "../../utils/sort.util";
import { AgentAvatar, AgentAvatarList } from "../Common/Avatar";
import Button from "../Common/Buttons";
import { FormattedDateTime } from "../Common/DateTime";
import DeadlineTag from "../Common/DeadlineTag";
import { Flex } from "../Common/Flex";
import Icon from "../Common/Icon";
import Label from "../Common/Label";
import HandleClicksOutside from "../HandleClicksOutside";
import "./ProfileDropdown.css";

import { useGet } from "@albertine/api-client";
import type { TaskDto } from "@albertine/types";
import { EventEmitter } from "../../EventEmitter";

function MentionListItem(props: { mention: TaskDto; agents: Record<string, Agent> }) {
    const { agents, mention } = props;
    const history = useHistory();
    const markedMember = useMembersMap()[mention.memberId];

    function goToMessage() {
        // Emitter call is needed so that scrolling into chat message works also within same url route
        EventEmitter.emit({
            name: "scrollChatIntoMessage",
            params: { messageId: mention.id },
        });
        history.push(
            routes.chatMessage(
                mention.memberId,
                mention.id,
                mention.requestId || undefined,
            ),
        );
    }

    return (
        <div
            className="mentions-list__item"
            onClick={() => {
                goToMessage();
            }}
        >
            <Flex.Row>
                <Label.Medium.SemiBold>
                    <span className="inline-flex items-center gap-1">
                        {mention.memberName}
                        <MemberMarkers
                            isTestMember={markedMember?.isTestMember === true}
                            isVipMember={markedMember?.isVipMember === true}
                        />
                    </span>
                    {mention.requestId ? `, ${mention.requestTitle || ""}` : null}
                </Label.Medium.SemiBold>
            </Flex.Row>
            <Flex.Row className="mentions-list__item-message">
                <Label.Small.Light>{mention.message}</Label.Small.Light>
            </Flex.Row>
            <Flex.Row gap="small">
                <AgentAvatarList
                    agents={mention.mentionedAgentIds
                        .map((a) => agents[a])
                        .filter((a) => !!a)}
                    numberOfAvatars={2}
                    showNames={false}
                />
                <DeadlineTag
                    deadlineAt={
                        mention.mentionDueDate
                            ? new Date(mention.mentionDueDate)
                            : undefined
                    }
                />
                {mention.mentionStatus === "open" ? (
                    <Label.Small.Light.N60>
                        {agents[mention.mentionCreatedBy]?.name} ·{" "}
                        <FormattedDateTime.Comparison
                            date={
                                mention.mentionCreatedAt
                                    ? new Date(mention.mentionCreatedAt)
                                    : undefined
                            }
                        />
                    </Label.Small.Light.N60>
                ) : (
                    <Label.Small.Light.N60>
                        {" "}
                        completed by {agents[mention.mentionResolvedBy!].name}{" "}
                        <FormattedDateTime.Default
                            date={
                                mention.mentionResolvedAt
                                    ? new Date(mention.mentionResolvedAt)
                                    : undefined
                            }
                        />
                    </Label.Small.Light.N60>
                )}
            </Flex.Row>
        </div>
    );
}

function useLogout() {
    const oidc = useAuth();
    return () => {
        if (window.confirm("Are you sure you want to log out?")) {
            signOut();
            // Flag the post-logout return so the Login screen renders the
            // manual button instead of auto-redirecting straight back.
            void oidc.signoutRedirect({
                post_logout_redirect_uri: `${window.location.origin}/?signedOut=1`,
            });
        }
    };
}

function MentionList(props: {
    currentAgent: Agent;
    pendingMentions: TaskDto[];
    close: () => void;
}) {
    const { currentAgent, pendingMentions: pengingMentions, close } = props;

    const [state, setState] = useState<"completed" | "pending">(
        pengingMentions.length === 0 ? "completed" : "pending",
    );
    const { data: completedMentionsResult } = useGet(
        "listTasks",
        {
            query: {
                sortBy: "mentionCreatedAt",
                sortOrder: "desc",
                page: "0",
                size: "10",
                mentionStatus: ["resolved"],
                assignedToAgentIds: [currentAgent.id],
            },
        },
        {
            enabled: state === "completed",
        },
    );
    const agents: Record<string, Agent> = useAgentsMap();

    const mentionsList =
        state === "pending" ? pengingMentions : completedMentionsResult?.tasks || [];

    if (Object.keys(agents).length === 0) return null;

    return (
        <>
            <div className="mentions-list__section-header">
                <div className="mentions-list__section-titles">
                    <div
                        className={classNames("mentions-list__section-title", {
                            selected: state === "pending",
                        })}
                        onClick={() => {
                            setState("pending");
                        }}
                    >
                        {pengingMentions.length !== 0 && (
                            <div className="profile-dropdown__mention-indicator">
                                {pengingMentions.length}
                            </div>
                        )}

                        <h4>Pending</h4>
                    </div>
                    <div
                        className={classNames("mentions-list__section-title", {
                            selected: state === "completed",
                        })}
                        onClick={() => {
                            setState("completed");
                        }}
                    >
                        <h4>Completed</h4>
                    </div>
                </div>
                <div className="mentions-list__section-close" onClick={close}>
                    <Icon.Close.Medium />
                </div>
            </div>
            <div className="mentions-list">
                {sortTasksInProfile(mentionsList).map((m) => (
                    <MentionListItem key={m.id} mention={m} agents={agents} />
                ))}
            </div>
        </>
    );
}

type Props = {
    currentAgent: Agent;
};

const ProfileDropdown = function ProfileDropdown(props: Props) {
    const { currentAgent } = props;
    const [dropdownOpened, setDropdownOpened] = useState(false);
    const history = useHistory();
    const logout = useLogout();
    const { data: pendingMentions } = useGet(
        "listTasks",
        {
            query: {
                sortBy: "mentionCreatedAt",
                sortOrder: "desc",
                page: "0",
                size: "1000",
                mentionStatus: ["open"],
                assignedToAgentIds: [currentAgent.id],
            },
        },
        { refetchInterval: 10000 },
    );
    const pendingMentionsCount = pendingMentions?.pagination.total || 0;

    return (
        <HandleClicksOutside
            onClickOutside={() => {
                setDropdownOpened(false);
            }}
            disabled={!dropdownOpened}
        >
            <div className="profile-dropdown__container">
                <button
                    type="button"
                    className="profile-dropdown"
                    onClick={() => {
                        setDropdownOpened(!dropdownOpened);
                    }}
                >
                    <div className="profile-dropdown__left">
                        <div className="profile-dropdown__avatar">
                            <div className="profile-dropdown__image">
                                <AgentAvatar agentId={currentAgent.id} size="small" />
                                {pendingMentionsCount > 0 && (
                                    <div className="profile-dropdown__mention-indicator">
                                        {pendingMentionsCount}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="profile-dropdown__name">
                            {currentAgent.name ?? ""}
                        </div>
                    </div>
                    <div className="profile-dropdown__icon">
                        {dropdownOpened ? (
                            <Icon.UnfoldLess.Medium />
                        ) : (
                            <Icon.UnfoldMore.Medium />
                        )}
                    </div>
                </button>

                {dropdownOpened ? (
                    <div className="profile-dropdown__actions-list">
                        <MentionList
                            pendingMentions={pendingMentions?.tasks || []}
                            close={() => {
                                setDropdownOpened(false);
                            }}
                            currentAgent={currentAgent}
                        />
                        <Button.Ghost
                            onClick={() => {
                                history.push("/preferences");
                            }}
                        >
                            Preferences
                        </Button.Ghost>
                        <Button.Ghost onClick={logout}>Log out</Button.Ghost>
                    </div>
                ) : null}
            </div>
        </HandleClicksOutside>
    );
};

export default ProfileDropdown;
